import React, { useState } from "react";
import {
  Table, Button, Space, Tag, message, Popconfirm,
  Form, Input, Drawer, Image, Upload
} from 'antd'
import { PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { useApiSelector } from 'src/hooks'
import { useRequest, dispatchAsync } from 'friday-async'
import jsCookie from 'js-cookie'
import config from 'src/config'

const { TextArea } = Input

const layout = {
  labelCol: { span: 6 },
  wrapperCol: { span: 18 },
};

const Benefit = () => {
  const apis = useApiSelector();
  const { dataArray, isValidating, revalidate } = useRequest(apis.benefit.list(), {
    paginated: false,
  })

  const [drawer, setDrawer] = useState<{ visible: boolean; info: any }>({
    visible: false, info: null
  })
  const [uploading, setUploading] = useState(false)
  const [form] = Form.useForm()
  const isEdit = !!drawer.info

  // ====== CRUD ======
  const handleCreate = () => setDrawer({ visible: true, info: null })

  const handleEdit = (record: any) => {
    setDrawer({ visible: true, info: record })
  }

  const handleSubmit = async () => {
    try {
      const vals = await form.validateFields()
      let error: any
      if (isEdit) {
        const r = await dispatchAsync(apis.benefit.update({ _id: drawer.info._id, ...vals }))
        error = r.error
      } else {
        const r = await dispatchAsync(apis.benefit.create(vals))
        error = r.error
      }
      if (error) return message.error(isEdit ? '编辑失败' : '创建失败')
      message.success(isEdit ? '编辑成功' : '创建成功')
      setDrawer({ visible: false, info: null })
      form.resetFields()
      revalidate()
    } catch (err) { console.log(err) }
  }

  const handleRemove = async (_id: string) => {
    const { error } = await dispatchAsync(apis.benefit.remove({ _id }))
    if (error) return message.error('删除失败')
    message.success('删除成功')
    revalidate()
  }

  // ====== 图片上传 ======
  const handleUpload = (info: any) => {
    if (info.file.status === 'uploading') {
      setUploading(true)
      return
    }
    if (info.file.status === 'done') {
      setUploading(false)
      const res = info.file.response
      if (res?.data?.url) {
        form.setFieldsValue({ image: res.data.url })
        message.success('图片上传成功')
      }
    } else if (info.file.status === 'error') {
      setUploading(false)
      message.error('图片上传失败')
    }
  }

  const imageUrl = Form.useWatch('image', form)

  // ====== 表格列 ======
  const columns = [
    { title: '权益名称', dataIndex: 'name', key: 'name', width: 200 },
    { title: '描述', dataIndex: 'description', key: 'description', width: 250, ellipsis: true },
    {
      title: '权益图片',
      dataIndex: 'image',
      key: 'image',
      width: 120,
      render: (v: string) => v ? (
        <Image
          src={`${config.imgHost}${v.startsWith('/') ? v.slice(1) : v}`}
          alt="权益图"
          width={60}
          height={60}
          style={{ objectFit: 'cover', borderRadius: 4 }}
          preview={{ mask: '预览' }}
        />
      ) : <Tag>-</Tag>,
    },
    { title: '创建时间', dataIndex: 'createTime', key: 'createTime', width: 180 },
    {
      title: '操作',
      key: 'action',
      width: 160,
      render: (_: any, record: any) => (
        <Space>
          <Button type="link" size="small" onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm title="确定删除该权益？" onConfirm={() => handleRemove(record._id)}>
            <Button type="link" size="small" danger>删除</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Button type="primary" ghost icon={<PlusOutlined />} onClick={handleCreate}>
        新建权益
      </Button>
      <Table
        rowKey="_id"
        columns={columns}
        dataSource={dataArray}
        loading={isValidating}
        pagination={false}
      />

      {/* 新增/编辑 抽屉 */}
      <Drawer
        title={isEdit ? '编辑权益' : '新建权益'}
        width={480}
        open={drawer.visible}
        onClose={() => {
          setDrawer({ visible: false, info: null })
          form.resetFields()
        }}
        destroyOnClose
        footer={
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button style={{ width: 120 }} onClick={() => {
              setDrawer({ visible: false, info: null })
              form.resetFields()
            }}>取消</Button>
            <Button style={{ width: 120 }} type="primary" onClick={handleSubmit}>确认</Button>
          </Space>
        }
      >
        <Form {...layout} form={form}
          initialValues={isEdit ? drawer.info : {}}
        >
          <Form.Item name="name" label="权益名称" rules={[{ required: true, message: '请输入权益名称' }]}>
            <Input placeholder="如 500元购车券" />
          </Form.Item>
          <Form.Item name="description" label="权益描述">
            <TextArea rows={3} placeholder="权益说明（可选）" />
          </Form.Item>
          <Form.Item name="image" label="权益图片">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Space>
                <Input
                  placeholder="输入图片链接，或点击上传"
                  style={{ width: 260 }}
                />
                <Upload
                  name="image"
                  showUploadList={false}
                  headers={{
                    Authorization: `Bearer ${jsCookie.get('token_jwt') || ''}`
                  }}
                  action={`${config.host}/api/platform/activity/uploadImage`}
                  onChange={handleUpload}
                >
                  <Button icon={<UploadOutlined />} loading={uploading}>上传</Button>
                </Upload>
              </Space>
              {imageUrl && (
                <Image
                  src={`${config.imgHost}${imageUrl.startsWith('/') ? imageUrl.slice(1) : imageUrl}`}
                  alt="预览"
                  style={{ maxWidth: 200, maxHeight: 120, objectFit: 'cover', borderRadius: 4, border: '1px solid #d9d9d9' }}
                  preview={{ mask: '预览' }}
                />
              )}
            </Space>
          </Form.Item>
        </Form>
      </Drawer>
    </Space>
  )
}

export default Benefit
